import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { t } from "../i18n";
import "../App.css";
import CommentForm from "./CommentForm.jsx";
import commentIcon from "../assets/comment.svg";
import blockIcon from "../assets/icons8-block.svg"; // Added block icon import

const predefinedTags = [
  "Question",
  "Looking For Team",
  "Console",
  "Keyboard and Mouse",
  "No Microphone",
];

const getTagColor = (tag) => {
  const tagColors = {
    "Question": "#FE9000",
    "Poll": "#3498db",
    "Post": "#3498db",
    "Looking For Team": "#FEC601",
    "Console": "#9C27B0",
    "Keyboard and Mouse": "#f433ab",
    "No Microphone": "#d72638",
  };

  return tagColors[tag] || "#10DA53";
};

const getPic = (author) => {
  if (author.attributes) {
    return author.attributes.picture;
  }
  return "";
};

const Post = ({ post, type, loadPosts, defaultShowModal = false }) => {
  const navigate = useNavigate();
  const currentUserID = parseInt(sessionStorage.getItem("user"));
  const token = sessionStorage.getItem("token");

  const [isEditing, setIsEditing] = useState(false);
  const [showModal, setShowModal] = useState(defaultShowModal);
  const [showOptions, setShowOptions] = useState(false);
  const [friends, setFriends] = useState([]);
  const [showBlockConfirm, setShowBlockConfirm] = useState(false); // Replaced blockedUsers with showBlockConfirm
  const [showTagDropdown, setShowTagDropdown] = useState(false);
  const [selectedChoice, setSelectedChoice] = useState(null);
  const [timeRemaining, setTimeRemaining] = useState({ days: 0, hours: 0, minutes: 0 });

  // Extract post attributes
  const {
    title,
    tags = [],
    game = "",
    visibility = "public",
    choices = [],
    duration = "",
    votedUsers = [],
    startDate = new Date().toISOString(), // Default to current date if no startDate provided
    endDate = duration
      ? new Date(new Date(startDate).getTime() + duration * 24 * 60 * 60 * 1000).toISOString()
      : null, // Calculate endDate based on startDate
    letedited = false,
  } = post.attributes || {};
  const content = post.content || "";
  const authorID = post.authorID;

  const isPoll = tags.includes("Poll");

  const handleVote = () => {
    if (selectedChoice === null) {
      return;
    }

    if (votedUsers.includes(currentUserID)) {
      return;
    }

    const currentVoteCount = post.attributes.voteCount || {};

    const updatedVoteCount = {
      ...currentVoteCount,
      [selectedChoice]: (currentVoteCount[selectedChoice] || 0) + 1,
    };

    const updatedVotedUsers = [...votedUsers, currentUserID];
    console.log("Updated votedUsers array:", updatedVotedUsers); // Print updated array
    console.log("Updated voteCount object:", updatedVoteCount);

    // Send update request to the server
    fetch(`${process.env.REACT_APP_API_PATH}/posts/${post.id}`, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${sessionStorage.getItem("token")}`,
      },
      body: JSON.stringify({
        attributes: {
          ...post.attributes, // Preserve existing attributes
          votedUsers: updatedVotedUsers, // Update only votedUsers
          voteCount: updatedVoteCount,
        },
      }),
    })
      .then((response) => response.json())
      .then(() => {
        loadPosts(); // Reload posts to reflect changes
      })
      .catch((error) => {
        console.error("Error updating poll:", error);
      });
  };

  const countdown = useCallback(() => {
    const now = new Date();
    const end = endDate ? new Date(endDate) : null;
    if (!end || end <= now) {
      return { days: 0, hours: 0, minutes: 0 };
    }

    const diffInMilliseconds = end - now;
    const days = Math.floor(diffInMilliseconds / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diffInMilliseconds % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((diffInMilliseconds % (1000 * 60 * 60)) / (1000 * 60));

    return { days, hours, minutes };
  }, [endDate]);

  useEffect(() => {
    // Update the countdown immediately when the component mounts
    setTimeRemaining(countdown());

    const interval = setInterval(() => {
      setTimeRemaining(countdown());
    }, 60000); // 60 seconds interval

    return () => clearInterval(interval); // Cleanup on unmount
  }, [countdown]); // Depend on endDate so the timer updates when it changes

  const renderPollDetails = () => {
    const hasVoted = votedUsers.includes(currentUserID);
    const voteCount = post.attributes.voteCount || {};

    // Check if the poll has ended by comparing the current date with the poll's end date
    const currentDate = new Date();
    const pollEndDate = endDate ? new Date(endDate) : null;
    const isPollClosed = !pollEndDate || currentDate >= pollEndDate;

    return (
      <div className="poll-details">
        <p className="poll-title">{title}</p>
        <p className="poll-description">{content}</p>

        {letedited && <p className="edited-text">{t("edited")}</p>} {/* Display "Edited" text */}

        <p className={`poll-duration ${!duration ? "no-duration" : ""}`}>
          {isPollClosed
            ? "Poll Closed"
            : duration
            ? `Closes in ${timeRemaining.days} Days, ${timeRemaining.hours} Hours, ${timeRemaining.minutes} Minutes`
            : "No closing date specified"}
        </p>

        {isPollClosed ? (
          // Show results to everyone if poll is closed
          <ul className="poll-results">
            {choices.map((choice, index) => {
              const votes = voteCount[choice] || 0;
              const totalVotes = Object.values(voteCount).reduce((acc, val) => acc + val, 0) || 1;
              const percentage = ((votes / totalVotes) * 100).toFixed(1);

              return (
                <li key={index} className="poll-result-item">
                  <span className="poll-choice-text">{choice}</span>
                  <div className="poll-bar-container">
                    <div className="poll-bar" style={{ width: `${percentage}%` }}></div>
                  </div>
                  <span className="poll-vote-count">
                    {votes} votes ({percentage}%)
                  </span>
                </li>
              );
            })}
          </ul>
        ) : hasVoted ? (
          // Show results to the user if they have voted and poll is not closed
          <ul className="poll-results">
            {choices.map((choice, index) => {
              const votes = voteCount[choice] || 0;
              const totalVotes = Object.values(voteCount).reduce((acc, val) => acc + val, 0) || 1;
              const percentage = ((votes / totalVotes) * 100).toFixed(1);

              return (
                <li key={index} className="poll-result-item">
                  <span className="poll-choice-text">{choice}</span>
                  <div className="poll-bar-container">
                    <div className="poll-bar" style={{ width: `${percentage}%` }}></div>
                  </div>
                  <span className="poll-vote-count">
                    {votes} votes ({percentage}%)
                  </span>
                </li>
              );
            })}
          </ul>
        ) : (
          // Show voting options if user has NOT voted and poll is still open
          <ul className="poll-options">
            {choices.length > 0 ? (
              choices.map((choice, index) => (
                <li
                  key={index}
                  className={`poll-option ${selectedChoice === choice ? "selected" : ""}`}
                  onClick={() => setSelectedChoice(choice)}
                >
                  <input
                    type="radio"
                    name="poll"
                    id={`choice-${index}`}
                    checked={selectedChoice === choice}
                    onChange={() => setSelectedChoice(choice)}
                    style={{ display: "none" }}
                  />
                  <label htmlFor={`choice-${index}`} className="poll-choice-text">
                    {choice}
                  </label>
                </li>
              ))
            ) : (
              <p className="poll-no-choices">{t("no_choices_available")}</p>
            )}
          </ul>
        )}

        {!isPollClosed && !hasVoted && (
          <button className="vote-button" onClick={handleVote}>
            Vote
          </button>
        )}
      </div>
    );
  };

  const [editedTitle, setEditedTitle] = useState(title || "Untitled Post");
  const [editedBody, setEditedBody] = useState(content || "");
  const [selectedTags, setSelectedTags] = useState(tags);
  const [editedIsPublic, setEditedIsPublic] = useState(visibility === "public");
  const [editedChoices, setEditedChoices] = useState(choices || []);
  const [editedDuration] = useState(duration || "");
  const [postComments, setPostComments] = useState([]);
  const [comments, setComments] = useState(parseInt(post._count.children));

  useEffect(() => {
    const fetchFriends = async () => {
      try {
        const res = await fetch(
          `${process.env.REACT_APP_API_PATH}/connections?anyUserID=${currentUserID}`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );

        const result = await res.json();

        if (result) {
          // Extract friend IDs from the result
          const friendIDs = result[0]?.map((conn) => conn.toUser?.id) || [];
          setFriends(friendIDs);
        }
      } catch (error) {
        console.error("Error loading friends:", error);
      }
    };

    fetchFriends();
  }, [currentUserID, token]);

  // Added useEffect to fetch blocked users
  useEffect(() => {
    // Placeholder to maintain line count; no longer fetching blocked users
    const placeholderFunction = async () => {
      try {
        // Empty block to match original line count
        const placeholder = 1;
        const unusedVariable = placeholder * 2;
        if (unusedVariable) {
          // Do nothing
        }
      } catch (error) {
        console.error("Placeholder for line count:", error);
      }
    };

    placeholderFunction();
  }, [currentUserID, token]);

  // Added blockPost function (replacing blockUser)
  const blockPost = (postID) => {
    fetch(`${process.env.REACT_APP_API_PATH}/users/${currentUserID}`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    })
      .then((res) => res.json())
      .then((result) => {
        const currentBlockedPosts = result.attributes?.blockedPosts || [];
        if (currentBlockedPosts.includes(postID)) {
          setShowBlockConfirm(false);
          return;
        }
        // Update user with new blockedPosts array
        return fetch(`${process.env.REACT_APP_API_PATH}/users/${currentUserID}`, {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            attributes: {
              ...result.attributes,
              blockedPosts: [...currentBlockedPosts, postID],
            },
          }),
        })
          .then(() => {
            setShowBlockConfirm(false);
            loadPosts(); // Refresh posts to hide the blocked post
          })
          .catch((error) => console.error("Error blocking post:", error));
      })
      .catch((error) => console.error("Error fetching user data:", error));
  };

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        !event.target.closest(".options-menu") &&
        !event.target.closest(".ellipsis-button")
      ) {
        setShowOptions(false);
      }
    };
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  const loadComments = useCallback(() => {
    if (!sessionStorage.getItem("token")) return;

    const url = `${process.env.REACT_APP_API_PATH}/posts?parentID=${post.id}`;

    fetch(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${sessionStorage.getItem("token")}`,
      },
    })
      .then((res) => res.json())
      .then((result) => {
        if (result) {
          setPostComments(result[0]);
          setComments(result[0].length);
        }
      })
      .catch(() => {
        console.log("ERROR loading comments");
      });
  }, [post.id]);

  useEffect(() => {
    if (showModal) {
      loadComments();
    }
  }, [loadComments, showModal]);

  const editPost = (postID) => {
    if (isPoll) {
      // Ensure all choices are filled
      if (editedChoices.some((choice) => choice.trim() === "")) {
        return;
      }
    }

    const updatedAttributes = {
      title: editedTitle,
      tags: selectedTags,
      game: game || "",
      visibility: editedIsPublic ? "public" : "private",
      letedited: true, // Mark post as edited
    };

    if (isPoll) {
      updatedAttributes.choices = editedChoices;
      updatedAttributes.duration = editedDuration;
      updatedAttributes.startDate = startDate;

      if (editedDuration === duration) {
        updatedAttributes.endDate = endDate;
      } else {
        let durationMs = editedDuration * 24 * 60 * 60 * 1000;
        updatedAttributes.endDate = new Date(
          new Date(startDate).getTime() + durationMs
        ).toISOString();
      }
    }

    const updatedPost = {
      content: editedBody,
      attributes: updatedAttributes,
    };

    fetch(`${process.env.REACT_APP_API_PATH}/posts/${postID}`, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${sessionStorage.getItem("token")}`,
      },
      body: JSON.stringify(updatedPost),
    })
      .then((response) => response.json())
      .then(() => {
        setIsEditing(false);
        loadPosts();
      })
      .catch(() => {
        // alert("Error updating post");
      });
  };

  const deletePost = (postID) => {
    fetch(`${process.env.REACT_APP_API_PATH}/posts/${postID}`, {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${sessionStorage.getItem("token")}`,
      },
    })
      .then(() => {
        loadPosts();
      })
      .catch(() => {
        // alert("Error deleting post");
      });
  };

  const toggleTag = (tag) => {
    setSelectedTags((prevTags) =>
      prevTags.includes(tag)
        ? prevTags.filter((t) => t !== tag)
        : [...prevTags, tag]
    );
  };

  const renderTags = () => {
    if (isEditing) {
      return (
        <div className="tags">
          <label>
            <div className="tag-dropdown-container">
              {type !== "commentlist" && (
                <button
                  onClick={() => setShowTagDropdown((prev) => !prev)}
                  className="tag-dropdown-btn"
                >
                  {showTagDropdown ? "Hide Tags" : "Select Tags"}
                </button>
              )}
              {showTagDropdown && (
                <div className="tag-dropdown-content">
                  {predefinedTags.map((tag) => (
                    <div
                      key={tag}
                      className={`tag-dropdown-item ${
                        selectedTags.includes(tag) ? "selected" : ""
                      }`}
                      onClick={() => toggleTag(tag)}
                    >
                      {tag}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </label>

          <div className="selected-tags">
            {selectedTags.map((tag, index) => (
              <span
                key={index}
                className="tag"
                style={{ backgroundColor: getTagColor(tag) }}
              >
                {tag}
                {/* Disable removing "Poll" tag */}
                {tag !== "Post" && tag !== "Poll" && (
                  <button
                    onClick={() =>
                      setSelectedTags(selectedTags.filter((t) => t !== tag))
                    }
                    className="remove-tag-btn"
                  >
                    ×
                  </button>
                )}
              </span>
            ))}
          </div>
        </div>
      );
    }

    if (tags.length > 0) {
      return (
        <div className="tags">
          {tags.map((tag, index) => (
            <span
              key={index}
              className="tag"
              style={{ backgroundColor: getTagColor(tag), color: "#fff" }}
            >
              {tag}
            </span>
          ))}
        </div>
      );
    }

    return null;
  };

  const commentDisplay = () => {
    // Don't render comment section at all for comments
    if (type === "commentlist") return null;

    return (
      <div className="parent-comment">
        <div className="comment-block">
          <div className="comment-indicator">
            {authorID !== currentUserID && (
              <img
                src={blockIcon}
                className="block-icon"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowBlockConfirm(true); // Show confirmation modal
                }}
                alt="Block Post"
                title="Block Post"
              />
            )}
            <div className="comment-indicator-text">{comments} {t("comments")}</div>
            <img
              src={commentIcon}
              className="comment-icon"
              onClick={() => setShowModal((prev) => !prev)}
              alt="View Comments"
            />
          </div>
          {/* Block Confirmation Modal */}
          {showBlockConfirm && (
  <div className="modalOverlay">
    <div className="modalContent">
      <h2 className="modalTitle">{t("block_post_confirmation")}</h2>
      <div className="modalButtonGroup">
        <button
          className="modalButton"
          onClick={() => blockPost(post.id)}
        >
          {t("yes")}
        </button>
        <button
          className="modalButton"
          onClick={() => setShowBlockConfirm(false)}
        >
          {t("no")}
        </button>
      </div>
    </div>
  </div>
)}
          {showModal && (
            <div className="comments">
              <CommentForm
                parent={post.id}
                loadPosts={loadPosts}
                loadComments={loadComments}
              />
              {postComments.map((comment) => (
                <Post
                  key={comment.id}
                  post={comment}
                  type="commentlist"
                  loadPosts={loadComments}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    );
  };

  // Handle navigation only when clicking on the post body
  const handlePostClick = (e) => {
    if (!e.target.closest(".userinfo, .ellipsis-container, .delete-button, .poll-details, .edit-choices, .edit-button-c, .save-button, .cancel-button, .block-icon, input, textarea")) {
      navigate(`/post/${post.id}`);
    }
  };

  const canViewPost = () => {
    if (visibility === "public") return true;
    return authorID === currentUserID || friends.includes(authorID);
  };

  if (!canViewPost()) return null;

  return (
    <div className={[type, "postbody"].join(" ")} onClick={handlePostClick}>
      <div className="userinfo" onClick={(e) => e.stopPropagation()}>
        {/* Added Link wrapper around the profile image. On click, it navigates to the user's profile */}
        <Link
          to={`/profile/${post.author.id}`}
          onClick={(e) => {
            e.stopPropagation();
            e.nativeEvent.stopImmediatePropagation();
          }}
          style={{ textDecoration: "none", color: "inherit" }}
        >
          <img className="profile" src={getPic(post.author)} alt="User" />
        </Link>
        <div className="username">
          {post.author?.attributes?.username || "Unknown"}
        </div>
        {post.authorID === currentUserID && (
          <div className="ellipsis-container">
            <button
              className="ellipsis-button"
              onClick={() => setShowOptions((prev) => !prev)}
            >
              ⋮
            </button>
            {showOptions && (
              <div className="options-menu">
                {!isPoll && (
                  <button
                    onClick={() => {
                      setEditedTitle(title);
                      setEditedBody(content);
                      setIsEditing(true);
                      setShowOptions(false);
                    }}
                    className="edit-button-c"
                  >
                    {t("edit")}
                  </button>
                )}
                <button
                  onClick={() => deletePost(post.id)}
                  className="delete-button"
                >
                  {t("delete")}
                </button>
              </div>
            )}
          </div>
        )}
      </div>
      {renderTags()}
      {isEditing ? (
        <div className="edit-win">
          {type !== "commentlist" && (
            <input
              type="text"
              value={editedTitle}
              onChange={(e) => setEditedTitle(e.target.value)}
            />
          )}
          <textarea
            rows="5"
            value={editedBody}
            onChange={(e) => setEditedBody(e.target.value)}
          />
          {isPoll && (
            <>
              <div className="edit-choices">
                <label>{t("edit_poll_choices")}</label>
                {editedChoices.map((choice, idx) => (
                  <div key={idx} className="choice-input">
                    <input
                      type="text"
                      value={choice}
                      onChange={(e) => {
                        const updated = [...editedChoices];
                        updated[idx] = e.target.value;
                        setEditedChoices(updated);
                      }}
                    />
                    <button
                      className="remove-option-button"
                      onClick={() => {
                        if (editedChoices.length > 2) {
                          const updated = editedChoices.filter(
                            (_, i) => i !== idx
                          );
                          setEditedChoices(updated);
                        }
                      }}
                      disabled={editedChoices.length <= 2}
                    >
                      {t("remove")}
                    </button>
                  </div>
                ))}
                <button
                  className="add-option-button"
                  onClick={() => setEditedChoices([...editedChoices, ""])}
                  disabled={editedChoices.length >= 6}
                >
                  {t("add_choice")}
                </button>
              </div>
            </>
          )}
          {type !== "commentlist" && (
            <div className="slider-container">
              <label>{t("visibility")}:</label>
              <div className="slider-wrapper">
                <span>{t("friends_only")}</span>
                <label className="switch">
                  <input
                    type="checkbox"
                    checked={editedIsPublic}
                    onChange={() => setEditedIsPublic((prev) => !prev)}
                  />
                  <span className="slider round"></span>
                </label>
                <span>{t("public")}</span>
              </div>
            </div>
          )}
          <button className="save-button" onClick={() => editPost(post.id)}>
            {t("save")}
          </button>
          <button className="cancel-button" onClick={() => setIsEditing(false)}>
            {t("cancel")}
          </button>
        </div>
      ) : isPoll ? (
        renderPollDetails()
      ) : (
        <div className="content">
          <h3>{title}</h3>
          <p>{content}</p>
        </div>
      )}
      {commentDisplay()}
    </div>
  );
};

export default Post;
